import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import clsx from "clsx";
import { useAuth } from "../../context/AuthContext.js";
import { PRIMARY_SECTIONS, getActivePrimaryId } from "../../nav/primary-nav.js";
import { useSidebarCollapse } from "./AppLayout.js";

export const PRIMARY_SIDEBAR_WIDTH = 80;

import { useHealth } from "../../hooks/useHealth.js";

const INDICATOR_STYLES = {
  online: { dot: "bg-green-400", label: "Online" },
  degraded: { dot: "bg-amber-400", label: "Degraded" },
  offline: { dot: "bg-red-400", label: "Offline" },
} as const;

export function PrimarySidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { indicator } = useHealth({ checkDegraded: true });
  const statusStyle = INDICATOR_STYLES[indicator];
  const { collapsed, toggle } = useSidebarCollapse();

  const activeId = getActivePrimaryId(location.pathname, location.search);

  const handleSignOut = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 z-50 flex flex-col items-center bg-primary-sidebar border-r border-primary-sidebar-border"
      style={{ width: PRIMARY_SIDEBAR_WIDTH }}
    >
      {/* Logo */}
      <div className="flex items-center justify-center h-16 w-full">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg p-1" style={{ background: "var(--primary-sidebar-logo-bg)" }}>
          <img src="/favicon.svg" alt="Identity Armour" className="h-full w-full" />
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 w-full overflow-y-auto py-2 px-1.5" aria-label="Primary navigation">
        <ul className="space-y-0.5">
          {PRIMARY_SECTIONS.map((section) => {
            const active = section.id === activeId;
            const Icon = section.icon;
            const defaultTo = section.links[0]?.to ?? "/";
            return (
              <li key={section.id}>
                <NavLink
                  to={defaultTo}
                  className={clsx(
                    "relative flex flex-col items-center justify-center gap-0.5 rounded-lg py-2.5 px-1 transition-all duration-150 group",
                    active
                      ? "text-primary-sidebar-active bg-primary-sidebar-active-bg shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                      : "text-primary-sidebar-muted hover:text-primary-sidebar-active hover:bg-primary-sidebar-hover"
                  )}
                  style={active && section.accent ? {
                    backgroundColor: `${section.accent}1a`,
                    color: section.accent,
                  } : undefined}
                  title={section.label}
                >
                  {/* Active left accent bar with section-specific color glow. */}
                  {active && (
                    <div
                      className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-[3px] rounded-r-full"
                      style={{
                        backgroundColor: section.accent ?? "var(--accent-primary)",
                        boxShadow: `0 0 10px 0 ${section.accent ?? "var(--accent-primary)"}`,
                      }}
                    />
                  )}
                  <Icon
                    className={clsx(
                      "h-5 w-5 flex-shrink-0 transition-transform duration-200",
                      active ? "scale-110" : "group-hover:scale-110"
                    )}
                    style={active && section.accent ? { color: section.accent } : undefined}
                    aria-hidden
                  />
                  <span className="text-[10px] font-medium leading-tight text-center">
                    {section.label}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer - Collapse toggle + Status + Sign out */}
      <div className="w-full px-2 py-3 border-t border-primary-sidebar-border flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          className="flex flex-col items-center justify-center gap-0.5 w-full rounded-lg py-2 px-1 text-primary-sidebar-muted hover:text-primary-sidebar-active hover:bg-primary-sidebar-hover transition-colors duration-150"
        >
          {collapsed
            ? <PanelLeftOpen className="h-4 w-4" aria-hidden />
            : <PanelLeftClose className="h-4 w-4" aria-hidden />}
          <span className="text-[9px] font-medium">{collapsed ? "Expand" : "Collapse"}</span>
        </button>
        <div className="flex items-center justify-center gap-1.5">
          <div className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`} />
          <span className="text-[9px] text-primary-sidebar-muted">{statusStyle.label}</span>
        </div>
        <button
          type="button"
          onClick={() => void handleSignOut()}
          title="Sign out"
          aria-label="Sign out"
          className="flex flex-col items-center justify-center gap-0.5 w-full rounded-lg py-2 px-1 text-primary-sidebar-muted hover:text-primary-sidebar-active hover:bg-primary-sidebar-hover transition-colors duration-150"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          <span className="text-[9px] font-medium">Sign out</span>
        </button>
      </div>
    </aside>
  );
}
